import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import Nav from "../components/app/Nav";
import Footer from "../components/app/Footer";
import FloatingButton from "../components/app/FloatingButton";
import FAQAccordion from "../components/app/FAQAccordion";
import "../styles/app/FAQPage.css";

const FAQPage: React.FC = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fullSchemaMarkup = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "¿Cuánto cuesta enviar un paquete a Venezuela?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Nuestras tarifas envíos aéreos comienzan desde $5.00 por libra y marítimos desde $15 por pie cúbico.",
        },
      },
      {
        "@type": "Question",
        name: "¿Qué artículos están PROHIBIDOS enviar a Venezuela?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Armas, explosivos, drogas, dinero en efectivo, joyas, carnes, embutidos y material pornográfico.",
        },
      },
      {
        "@type": "Question",
        name: "¿Cómo se calcula el peso volumétrico?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Largo x Ancho x Alto (pulgadas) / 166. Cobramos el mayor entre peso real y volumétrico.",
        },
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>
          Preguntas Frecuentes | Envíos a Latinoamérica | elPaket
        </title>
        <meta
          name="description"
          content="Resolvemos todas tus dudas sobre envíos desde USA a Venezuela, República Dominicana, Costa Rica, Guatemala y Perú: Tarifas, Recogida UPS, Tiempos, Artículos Prohibidos y Aduanas."
        />
        <link rel="canonical" href="https://elpaket.com/preguntas-frecuentes" />
        <script type="application/ld+json">
          {JSON.stringify(fullSchemaMarkup)}
        </script>
      </Helmet>

      <Nav />

      <main className="faq-page-main">
        <div className="faq-page-header">
          <p className="header-brand">elPaket</p>
          <h1>Centro de Ayuda y Preguntas Frecuentes</h1>
          <p className="faq-subtitle-hero">
            Todo lo que necesitas saber antes de enviar a Venezuela
          </p>
        </div>

        <div className="faq-content-container">
          {/* Sección 1: Información General */}
          <div className="faq-category-section">
            <h2>Información General</h2>
            <details className="faq-detail-item">
              <summary>1. ¿Por qué elegir elPaket?</summary>
              <p>
                Ofrecemos tarifas transparentes, tracking real 24/7, seguro y
                cobertura en más de 300 destinos.
              </p>
            </details>
            <details className="faq-detail-item">
              <summary>2. ¿Qué es el servicio puerta a puerta?</summary>
              <p>
                Es un método donde recogemos (o recibimos) tu paquete en USA y
                lo entregamos directamente en la dirección del destinatario en
                Latinoamérica. Nos encargamos de toda la logística y aduana.
              </p>
            </details>
          </div>

          {/* Sección 2: Tarifas */}
          <div className="faq-category-section">
            <h2>Tarifas y Costos</h2>
            <details className="faq-detail-item">
              <summary>3. ¿Cómo se calculan los costos de envío?</summary>
              <p>
                El costo final depende del peso y del espacio que ocupe tu
                paquete en el transporte. Siempre se cobrará el valor más alto
                entre el peso físico (báscula) y el peso volumétrico
                (dimensiones).
                <br />
                <br />
                No te preocupes por hacer cuentas: puedes obtener el costo
                estimado de forma automática usando nuestra{" "}
                <Link to="/calculator">Calculadora de Envíos en línea</Link>.
                ¡Solo necesitas ingresar las medidas de tu paquete!
              </p>
            </details>
            <details className="faq-detail-item">
              <summary>4. ¿Existen costos ocultos?</summary>
              <p>
                No. El precio incluye transporte y entrega. Cargos extra solo
                aplican por: reempaque solicitado o impuestos en artículos de
                alto valor (laptops, etc). Los costos de envío están sujetos a
                variaciones. Si el peso, las medidas o el valor declarado
                indicados originalmente son incorrectos, se aplicará un reajuste
                en el precio final de su orden tras la auditoría física de la
                carga.
              </p>
            </details>
          </div>

          {/* Sección 3: Tiempos */}
          <div className="faq-category-section">
            <h2>Tiempos de Entrega</h2>
            <details className="faq-detail-item">
              <summary>5. ¿Cuánto tarda en llegar?</summary>
              <p>
                Aéreo: 7-15 días hábiles. Marítimo: 4-6 semanas. Puede variar
                por aduanas o temporada.
              </p>
            </details>
            <details className="faq-detail-item">
              <summary>6. ¿Puedo rastrear mi paquete?</summary>
              <p>
                Sí, en tiempo real ingresando tu número de guía en nuestra
                página de Rastreo. Recibirás notificaciones por email.
              </p>
            </details>
          </div>

          {/* Sección 4: Qué Enviar */}
          <div className="faq-category-section">
            <h2>Qué puedes enviar</h2>
            <details className="faq-detail-item">
              <summary>7. Artículos Permitidos</summary>
              <p>
                Ropa, electrónicos, juguetes, comida seca, medicinas (con
                receta), repuestos, herramientas.
              </p>
            </details>
            <details className="faq-detail-item">
              <summary>8. Artículos PROHIBIDOS 🚫</summary>
              <p>
                Armas, drogas, dinero, joyas, carnes/embutidos, aerosoles,
                material inflamable o pornográfico.
              </p>
            </details>
            <details className="faq-detail-item">
              <summary>9. ¿Puedo enviar medicinas?</summary>
              <p>
                Sí, cantidad personal (máx 3 meses) con receta médica copia
                adjunta.
              </p>
            </details>
          </div>

          {/* Sección 5: Documentos */}
          <div className="faq-category-section">
            <h2>Documentos y Aduanas</h2>
            <details className="faq-detail-item">
              <summary>10. ¿Qué documentos necesito?</summary>
              <p>
                Remitente: ID válido. Destinatario: Cédula, dirección completa y
                teléfono.
              </p>
            </details>
            <details className="faq-detail-item">
              <summary>11. ¿Pago impuestos?</summary>
              <div className="faq-table-wrapper">
                <table className="faq-table">
                  <thead>
                    <tr>
                      <th>País</th>
                      <th>Límite Exento (De Minimis)</th>
                      <th>Impuesto a las Ventas / IVA</th>
                      <th>Arancel Promedio (DAI)</th>
                      <th>Método de Cálculo aduanero</th>
                      <th>Nota Especial u Observación</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <strong>República Dominicana</strong>
                      </td>
                      <td>Hasta $200 USD</td>
                      <td>18% (ITBIS)</td>
                      <td>0% a 20%</td>
                      <td>Valor del artículo (FOB)</td>
                      <td>
                        Exención aplica solo para envíos de uso personal no
                        comercial.
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Costa Rica</strong>
                      </td>
                      <td>Hasta $500 USD</td>
                      <td>13% (IVA)</td>
                      <td>0% a 15%</td>
                      <td>Valor + Seguro + Flete (CIF)</td>
                      <td>
                        El beneficio de exención se permite solo una vez cada 6
                        meses.
                      </td>
                    </tr>
                    {/*
                    <tr>
                      <td>
                        <strong>Cuba</strong>
                      </td>
                      <td>Hasta $30 USD (3 kg)</td>
                      <td>No aplica</td>
                      <td>30% (sobre excedente)</td>
                      <td>Peso / Valor asignado aduana</td>
                      <td>
                        Alimentos, medicinas y aseo tienen exención total de
                        aranceles.
                      </td>
                    </tr>
                    */}
                    <tr>
                      <td>
                        <strong>Guatemala</strong>
                      </td>
                      <td>No tiene (todo paga)</td>
                      <td>12% (IVA)</td>
                      <td>0% a 15%</td>
                      <td>Valor + Seguro + Flete (CIF)</td>
                      <td>
                        Paquetes de $1,000 USD o más requieren agente de
                        aduanas.
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Venezuela</strong>
                      </td>
                      <td>Hasta $100 USD</td>
                      <td>16% (IVA)</td>
                      <td>Variable según rubro</td>
                      <td>Valor de la mercancía</td>
                      <td>
                        Agencias "Puerta a Puerta" cobran tarifa fija con
                        impuesto incluido.
                      </td>
                    </tr>
                  </tbody>
                </table>
                <p className="faq-table-footnote">
                  ⚠️ Los valores descritos son referenciales y pueden cambiar en
                  cualquier momento debido a las leyes o reglas del país
                  destino.
                </p>
              </div>
            </details>
          </div>

          {/* Sección 6: Seguridad */}
          <div className="faq-category-section">
            <h2>Seguridad y Seguros</h2>
            <details className="faq-detail-item">
              <summary>12. ¿Ofrecen seguro?</summary>
              <p>
                Si, Ponemos a su disposición un servicio de seguro de mercancía
                cuya tarifa y nivel de cobertura se determinan en proporción
                directa al valor declarado de los Bienes.
              </p>
            </details>
            <details className="faq-detail-item">
              <summary>13. ¿Qué hago si se pierde?</summary>
              <p>
                En Caso de Extravío o Pérdida de Envíos se iniciará de forma
                automática un proceso de auditoría e investigación interna. Este
                procedimiento tiene un tiempo estimado de resolución de 7 a 15
                días hábiles. Una vez concluido el análisis y determinada la
                pérdida definitiva, se procederá con el reembolso del 100% del
                valor declarado por el cliente al momento de contratar el
                servicio.
              </p>
            </details>
          </div>

          {/* CTA Section */}
          <div className="faq-contact-cta">
            <h3>¿Tienes dudas sobre algún artículo?</h3>
            <p>Contáctanos antes de enviar y te asesoraremos sin compromiso.</p>
            <div className="faq-contact-buttons">
              <a
                href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER || "17548005041"}`}
                className="btn-whatsapp-faq"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faPhone} /> Consultar por WhatsApp
              </a>
              <Link to="/" className="btn-back-faq">
                <FontAwesomeIcon icon={faArrowLeft} /> Volver al Inicio
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/*
      <FloatingButton />
      */}
      <Footer />
    </>
  );
};

export default FAQPage;
